import { useEffect, useRef, useState, useCallback, forwardRef, memo, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Modal, Input, Typography, Space, Button, Grid } from 'antd';
import { toast } from 'react-toastify';

import tecnologia from '../../assets/css/template.tecnologia.module.css';
import banner from '../../assets/css/banner.module.css';

import dataRedirect from '../../data/json/modalRedirect.json';
import data from '../../data/json/template-tecnologia.json';

import reclameaqui from '../../assets/imagens/svg/selo-reclame-aqui.svg';
import google from '../../assets/imagens/svg/selo-google-review.svg';

import cliente2 from '../../assets/imagens/webp/julliano-pereira.webp';
import cliente1 from '../../assets/imagens/webp/joany-peres.webp';

import WhatsAppButton from '../BotaoWhatsapp';
import Formulario from '../Formulario';
import Footer from '../Footer';
import Logo from '../Logo';

import { BiDownArrowAlt, BiUpArrowAlt } from 'react-icons/bi';
import { RiWhatsappLine, RiUserLine } from 'react-icons/ri';
import { GoArrowLeft } from 'react-icons/go';
import { FaRocket } from 'react-icons/fa';

const { useBreakpoint } = Grid;

const { Title, Text } = Typography;
const { page } = data;

/* ══════════════════════════════════════
   MAPA: selectedId → selectedOption
══════════════════════════════════════ */
const OPTION_MAP = {
  '1': { id: 'abrir', description_form: 'abertura do CNPJ' },
  '2': { id: 'trocar', description_form: 'troca de contabilidade' },
  '3': { id: 'migrar', description_form: 'migração de MEI para ME' },
};

// ✅ PERF: Componente estável — evita criar novo objeto de props a cada render
const InlineHtml = memo(({ str }) => (
  <span dangerouslySetInnerHTML={{ __html: str }} />
));
InlineHtml.displayName = 'InlineHtml';

// ✅ PERF: função pura fora do componente — não recria a cada render
function parseTitleWithTags(str) {
  const parts = str
    .replace(/<grad>(.*?)<\/grad>/g, '|||GRAD:$1|||')
    .replace(/<pink>(.*?)<\/pink>/g, '|||PINK:$1|||')
    .split('|||');

  return parts.map((part, i) => {
    if (part.startsWith('GRAD:'))
      return <span key={i} className={tecnologia['grad-text']}>{part.slice(5)}</span>;
    if (part.startsWith('PINK:'))
      return <span key={i} className={tecnologia['pink-text']}>{part.slice(5)}</span>;
    return <span key={i}>{part}</span>;
  });
}

// ✅ PERF: Wrapper memoizado — só re-parseia quando `str` muda
const TitleWithTags = memo(({ str }) => {
  const parts = useMemo(() => parseTitleWithTags(str), [str]);
  return <>{parts}</>;
});
TitleWithTags.displayName = 'TitleWithTags';

/* ── ScrollArrow ── */
// ✅ PERF: memo — não re-renderiza se props não mudaram
const ScrollArrow = memo(({ onClick, direction = 'down' }) => {
  const isDown = direction === 'down';
  return (
    <div className={tecnologia['scroll-arrow-wrap']}>
      <div className={tecnologia['terminal-hint']}>
        <kbd
          className={tecnologia['pulse-btn']}
          onClick={onClick}
          aria-label={isDown ? 'Próxima seção' : 'Voltar ao topo'}
        >
          {isDown
            ? <BiDownArrowAlt style={{ fontSize: 18 }} />
            : <BiUpArrowAlt style={{ fontSize: 18 }} />
          }
        </kbd>
      </div>
    </div>
  );
});
ScrollArrow.displayName = 'ScrollArrow';

/* ── TerminalLine ── */
// ✅ PERF: memo — linhas do terminal são estáticas após montagem
const TerminalLine = memo(({ line, index }) => {
  const style = { '--line-delay': `${0.5 + index * 0.1}s` };

  const inner = (() => {
    switch (line.type) {
      case 'keyval': {
        const valClass = {
          string: tecnologia['t-string'],
          success: tecnologia['t-success'],
          val: tecnologia['t-val'],
        }[line.valType] || tecnologia['t-val'];
        return (
          <>
            <span className={tecnologia['t-prompt']}>  </span>
            <span className={tecnologia['t-key']}>{line.key}</span>
            <span className={tecnologia['t-prompt']}>) </span>
            <span className={valClass}>{line.val}</span>
          </>
        );
      }
      case 'success':
        return <span className={tecnologia['t-success']}>{line.text}</span>;
      case 'command':
        return (
          <>
            <span className={tecnologia['t-prompt']}>$ </span>
            <span className={tecnologia['t-cmd']}>{line.text}</span>
          </>
        );
      case 'comment':
        return <span className={tecnologia['t-comment']}>{line.text}</span>;
      default:
        return null;
    }
  })();

  if (line.type === 'break') return <br />;
  return <div className={tecnologia['terminal-line']} style={style}>{inner}</div>;
});
TerminalLine.displayName = 'TerminalLine';

/* ══════════════════════════════════════
   TERMINAL CLICÁVEL
══════════════════════════════════════ */
// ✅ PERF: headerLines e optionLines calculados fora do componente (dados estáticos do JSON)
const _terminalHeaderLines = page.hero.terminal.lines.filter(l => l.type !== 'input_prompt' && l.type !== 'keyval');
const _terminalOptionLines = page.hero.terminal.lines.filter(l => l.type === 'keyval');

function InteractiveTerminal({ onExecuteScroll }) {
  const { terminal } = page.hero;
  const [phase, setPhase] = useState('idle');
  const [selectedOption, setSelected] = useState(null);
  const [hoveredOption, setHovered] = useState(null);
  const redirectTimerRef = useRef(null);

  const feedbackLines = useMemo(
    () => selectedOption ? terminal.options[selectedOption].successLines : [],
    [selectedOption, terminal.options]
  );

  const handleSelect = useCallback((key) => {
    if (redirectTimerRef.current) clearTimeout(redirectTimerRef.current);

    setSelected(key);
    setPhase('selected');

    if (onExecuteScroll) onExecuteScroll(key);

    // ← SEM setTimeout de redirect aqui
    // O navigate já acontece no pai via onExecuteScroll
  }, [onExecuteScroll, terminal.options]);

  useEffect(() => {
    return () => {
      if (redirectTimerRef.current) clearTimeout(redirectTimerRef.current);
    };
  }, []);

  return (
    <div className={tecnologia.terminal} style={{ border: "2px solid #575d64" }}>
      <div className={tecnologia['terminal-bar']}>
        <span className={`${tecnologia['t-dot']} ${tecnologia['t-dot-red']}`} aria-hidden="true" />
        <span className={`${tecnologia['t-dot']} ${tecnologia['t-dot-yellow']}`} aria-hidden="true" />
        <span className={`${tecnologia['t-dot']} ${tecnologia['t-dot-green']}`} aria-hidden="true" />
        <span className={tecnologia['terminal-title']}>{terminal.title}</span>
      </div>

      <div className={tecnologia['terminal-body']} role="application" aria-label="Terminal — clique em uma opção">
        {_terminalHeaderLines.map((line, i) => <TerminalLine key={i} line={line} index={i} />)}

        {_terminalOptionLines.map((line) => {
          const isSelected = line.key === selectedOption;
          const isDimmed = selectedOption && !isSelected && phase !== 'redirecting';

          return (
            <button
              key={line.key}
              disabled={phase === 'redirecting'}
              className={`${tecnologia['terminal-option-btn']} ${isSelected
                  ? tecnologia['terminal-option-selected']
                  : isDimmed
                    ? tecnologia['terminal-option-dimmed']
                    : hoveredOption === line.key
                      ? tecnologia['terminal-option-hovered']
                      : ''
                }`}
              onClick={() => handleSelect(line.key)}
              onMouseEnter={() => setHovered(line.key)}
              onMouseLeave={() => setHovered(null)}
            >
              <span className={tecnologia['t-option-prompt']}>
                {isSelected || hoveredOption === line.key ? '▶' : ' '}
              </span>
              <span className={tecnologia['t-option-key']}>{line.key}</span>
              <span className={tecnologia['t-prompt']}>) </span>
              <span className={tecnologia['t-string']}>
                {line.val}
                <strong style={{ color: "#e8ac22" }}> CLIQUE AQUI</strong>
              </span>
            </button>
          );
        })}

        {/* Um único bloco de feedback */}
        {(phase === 'selected' || phase === 'redirecting') && (
          <>
            <br />
            {feedbackLines.map((line, i) => (
              <TerminalLine key={`fb-${i}`} line={line} index={i} />
            ))}
            {phase === 'redirecting' && (
              <div className={tecnologia['terminal-line']} style={{ '--line-delay': `${feedbackLines.length * 0.1 + 0.2}s` }}>
                <span className={tecnologia['t-comment']}>
                  // carregando informações
                  <span className={tecnologia['t-cursor']} aria-hidden="true" />
                </span>
              </div>
            )}
          </>
        )}
      </div>

      {!selectedOption && (
        <div className={tecnologia['terminal-hint']}>clique em uma opção para continuar</div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════
   PAINEL TECH "IDEAL PARA"
══════════════════════════════════════ */
const IDEAL_TAG_MAP = {
  '1': [
    { label: 'abertura_CNPJ', type: 'blue' },
    { label: 'zero_burocracia', type: 'green' },
    { label: 'compliance_first', type: 'cyan' },
  ],
  '2': [
    { label: 'troca_simples', type: 'blue' },
    { label: 'sem_fidelidade', type: 'green' },
    { label: 'suporte_realtime', type: 'pink' },
  ],
  '3': [
    { label: 'migração_MEI→ME', type: 'cyan' },
    { label: 'regime_otimizado', type: 'blue' },
    { label: 'observability', type: 'green' },
  ],
};

// ✅ PERF: lazy import da imagem pim.webp — só carrega quando IdealParaPanel é montado
const PIM_SRC = new URL('../../assets/imagens/webp/pim.webp', import.meta.url).href;

const IdealParaPanel = memo(({ optionData, selectedId }) => {
  const tags = IDEAL_TAG_MAP[selectedId] || [];

  return (
    <div className={tecnologia['ideal-panel']}>
      <div className={tecnologia['ideal-blob-1']} aria-hidden="true" />
      <div className={tecnologia['ideal-blob-2']} aria-hidden="true" />
      <div className={tecnologia['ideal-grid-bg']} aria-hidden="true" />
      <div className={tecnologia['ideal-scanline']} aria-hidden="true" />

      <div className={tecnologia['ideal-left']}>
        <div className={tecnologia['ideal-terminal-bar']}>
          <span className={`${tecnologia['t-dot']} ${tecnologia['t-dot-red']}`} />
          <span className={`${tecnologia['t-dot']} ${tecnologia['t-dot-yellow']}`} />
          <span className={`${tecnologia['t-dot']} ${tecnologia['t-dot-green']}`} />
          <span className={tecnologia['ideal-terminal-title']}>// profile.sys</span>
        </div>
        <div className={tecnologia['ideal-status-badge']}>
          <span className={tecnologia['ideal-pulse-dot']} />
          sistema_online
        </div>
        <div className={tecnologia['ideal-section-label']}>// dev_profile</div>
        <div className={tecnologia['ideal-avatar-block']}>
          <div className={tecnologia['ideal-avatar-circle']}>
            <div className={tecnologia['ideal-avatar-ring']} />
            {/* ✅ PERF:  + decoding="sync" para não bloquear LCP */}
            <img
              src={PIM_SRC}
              alt="Dev"
              className={tecnologia['ideal-avatar-img']}
              fetchpriority="high"
              decoding="sync"
              width={48}
              height={48}
            />
          </div>
          <div className={tecnologia['ideal-avatar-info']}>
            <div className={tecnologia['ideal-avatar-name']}>dev.user</div>
            <div className={tecnologia['ideal-avatar-role']}>// profissional_PJ</div>
            <div className={tecnologia['ideal-avatar-tag']}>
              <span style={{ color: 'var(--green)' }}>●</span> perfil_ativo
            </div>
          </div>
        </div>
        <div className={tecnologia['ideal-tags-row']}>
          {tags.map((tag, i) => (
            <span key={i} className={`${tecnologia['ideal-tag']} ${tecnologia[`ideal-tag-${tag.type}`]}`}>
              {tag.label}
            </span>
          ))}
        </div>
        <div className={tecnologia['ideal-typing-line']}>
          <span className={tecnologia['t-prompt']}>$</span>
          <span className={tecnologia['t-cmd']}> mco</span>
          <span style={{ color: 'var(--text3)', fontFamily: 'var(--mono)', fontSize: '11px' }}> match --profile</span>
          <span className={tecnologia['ideal-typing-cursor']} />
        </div>
      </div>

      <div className={tecnologia['ideal-right']}>
        <div className={tecnologia['ideal-right-header']}>
          <span className={tecnologia['ideal-right-title']}>
            // targets.json · {optionData.ideais?.length || 0} matches
          </span>
          <span className={tecnologia['ideal-badge-yellow']}>Ideal para...</span>
        </div>
        <div className={tecnologia['ideal-cards-list']}>
          {optionData.ideais && optionData.ideais.map((ideal, index) => (
            <div key={ideal.id} className={tecnologia['ideal-profile-card']}>
              <div className={tecnologia['ideal-check-wrap']}>
                <span className={tecnologia['ideal-check-icon']}>✓</span>
              </div>
              <p className={tecnologia['ideal-card-text']}>{ideal.text}</p>
              <span className={tecnologia['ideal-card-index']}>[0x0{index + 1}]</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
});
IdealParaPanel.displayName = 'IdealParaPanel';

/* ══════════════════════════════════════
   HOOK — scroll reveal
   ✅ PERF: IntersectionObserver reutilizado, sem recriar a cada render
══════════════════════════════════════ */
function useScrollReveal(dep) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // ✅ PERF: desconecta observer anterior antes de criar novo
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => {
        if (e.isIntersecting) {
          e.target.classList.add(tecnologia['in-view']);
          observer.unobserve(e.target);
        }
      }),
      { threshold: 0.08, rootMargin: '0px 0px -32px 0px' }
    );

    const nodes = el.querySelectorAll(`.${tecnologia['animate-on-scroll']}`);
    nodes.forEach((node) => {
      node.classList.remove(tecnologia['in-view']);
      observer.observe(node);
    });

    return () => observer.disconnect();
  }, [dep]);
  return ref;
}

/* ══════════════════════════════════════
   SEÇÕES — forwardRef + memo
   ✅ PERF: todas as seções memoizadas para evitar re-render desnecessário
══════════════════════════════════════ */

/* ── Banner ── */
const Banner = memo(forwardRef(({ selectedId, onReset, onScrollDown, onScrollToForm }, ref) => {
  if (!selectedId) return null;
  const optionData = page.hero.terminal.options[selectedId];

  return (
    <section ref={ref} id="banner-alvo" className={`${tecnologia.section} ${tecnologia['banner-section']}`}>
      <div className={tecnologia.container}>
        <div className={tecnologia['banner-grid']}>
          <div className={tecnologia['banner-info']}>
            <div style={{ display: 'flex' }}>
              <div className={tecnologia['terminal-hint']} onClick={onReset} style={{ cursor: 'pointer' }}>
                <kbd><GoArrowLeft /></kbd> Voltar
              </div>
            </div>

            <div className={tecnologia['section-label']}>
              // opção_selecionada:{' '}
              <Link style={{ textDecoration: 'none' }} to={`/${optionData.url_section}`}>
                <span className={tecnologia['banner-option-highlight']}>
                  {selectedId === '1' && 'Abrir Empresa'}
                  {selectedId === '2' && 'Trocar de Contabilidade'}
                  {selectedId === '3' && 'Migrar MEI para ME'}
                </span>
              </Link>
            </div>

            <h2 className={tecnologia['banner-title']}>{optionData.label}</h2>
            <p className={tecnologia['hero-sub']}>{optionData.sub}</p>

            <div className={tecnologia['banner-price-block']}>
              {optionData.mensalidade && (
                <span className={tecnologia['banner-price-label']}>{optionData.mensalidade}</span>
              )}
              <div className={tecnologia['banner-price-value']}>
                <span className={tecnologia['banner-price-currency']}>R$</span>
                <span className={tecnologia['banner-price-number']}>{optionData.preco}</span>
                <span className={tecnologia['banner-price-period']}>/MÊS</span>
              </div>
            </div>

            <button
              onClick={onScrollToForm}
              className={`${tecnologia['btn-primary']} ${tecnologia['btn-primary-large']}`}
            >
              <FaRocket /> {optionData.btn}
            </button>
          </div>

          <IdealParaPanel optionData={optionData} selectedId={selectedId} />
        </div>
      </div>
      <ScrollArrow onClick={onScrollDown} direction="down" />
    </section>
  );
}));
Banner.displayName = 'Banner';

/* ── Como Funciona ── */
const SectionComoFunciona = memo(forwardRef(({ selectedId, onScrollDown, onScrollToForm }, ref) => {
  if (!selectedId) return null;

  const optionData = page.hero.terminal.options[selectedId];

  return (
    <section ref={ref} id="como-funciona" className={`${tecnologia.section} ${tecnologia['como-funciona-section']}`}>
      <div className={tecnologia.container}>
        <div className={`${tecnologia['section-header']} ${tecnologia['section-header-center']} ${tecnologia['animate-on-scroll']}`}>
          <div className={tecnologia['section-label']}>{page.comoFunciona.sectionLabel}</div>
          <h2 className={tecnologia['section-title']}>
            <TitleWithTags str={page.comoFunciona.title} />
          </h2>
          <p className={tecnologia['section-sub']}>{page.comoFunciona.subtitle}</p>
        </div>
        <div className={tecnologia['steps-grid']}>
          {page.comoFunciona.steps.map((step, i) => (
            <div
              key={step.id}
              className={`${tecnologia['step-card']} ${tecnologia['animate-on-scroll']}`}
              style={{ transitionDelay: `${i * 0.15}s` }}
            >
              <div className={tecnologia['step-num']} aria-hidden="true">{step.num}</div>
              <h3 className={tecnologia['step-title']}>{step.title}</h3>
              <p className={tecnologia['step-desc']}>{step.desc}</p>
              <div className={tecnologia['step-terminal']} role="img" aria-label={`Passo ${step.num}: ${step.title}`}>
                {step.terminal.map((line, j) => <TerminalLine key={j} line={line} index={j} />)}
              </div>
            </div>
          ))}
        </div>

        <div style={{ padding: 10, display: "flex", justifyContent: "center" }}>
          <button
            onClick={onScrollToForm}
            className={`${tecnologia['btn-primary']} ${tecnologia['btn-primary-large']}`}
          >
            <FaRocket /> {optionData.btn}
          </button>
        </div>
      </div>
      <ScrollArrow onClick={onScrollDown} direction="down" />
    </section>
  );
}));
SectionComoFunciona.displayName = 'SectionComoFunciona';

/* ── Diferenciais ── */
const SectionDiferenciais = memo(forwardRef(({ selectedId, onScrollDown, onScrollToForm }, ref) => {
  if (!selectedId) return null;
  const optionData = page.hero.terminal.options[selectedId];

  return (
    <section
      ref={ref}
      id="diferenciais"
      className={`${tecnologia.section} ${tecnologia['diferenciais-section']}`}
      style={{ padding: '64px 0 0', marginBottom: 30 }}
    >
      <div className={`${tecnologia.blob} ${tecnologia['diferenciais-blob1']}`} aria-hidden="true" />
      <div className={tecnologia.container}>
        <div className={tecnologia['difs-grid']}>
          <div>
            <div className={`${tecnologia['section-label']} ${tecnologia['animate-on-scroll']}`}>{page.diferenciais.sectionLabel}</div>
            <h2 className={`${tecnologia['section-title']} ${tecnologia['animate-on-scroll']}`}>
              <TitleWithTags str={page.diferenciais.title} />
            </h2>
            <p className={`${tecnologia['section-sub']} ${tecnologia['animate-on-scroll']}`} style={{ marginBottom: 0 }}>{page.diferenciais.subtitle}</p>
            <div className={tecnologia['difs-list']}>
              {page.diferenciais.list.map((item, i) => (
                <div
                  key={item.id}
                  className={`${tecnologia['dif-item']} ${tecnologia['animate-on-scroll']}`}
                  style={{ transitionDelay: `${i * 0.1}s` }}
                >
                  <div className={tecnologia['dif-check']} aria-hidden="true">✓</div>
                  <div>
                    <h3 className={tecnologia['dif-title']}>{item.title}</h3>
                    <p className={tecnologia['dif-desc']}>{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className={tecnologia['animate-on-scroll']} style={{ transitionDelay: '0.2s' }}>
            <div className={tecnologia['difs-compare']}>
              <div className={tecnologia['compare-header']}>
                {page.diferenciais.compareTable.headers.map((h, i) => (
                  <div key={i} className={i === 1 ? tecnologia['th-mco'] : i === 2 ? tecnologia['th-trad'] : ''}>{h}</div>
                ))}
              </div>
              {page.diferenciais.compareTable.rows.map((row, i) => (
                <div key={i} className={tecnologia['compare-row']}>
                  <div className={tecnologia['compare-feature']}>{row.feature}</div>
                  <div className={tecnologia['compare-yes']}>{row.mco}</div>
                  <div className={tecnologia['compare-no']}>{row.trad}</div>
                </div>
              ))}
            </div>
            <br />
            <div style={{ padding: 10 }}>
              <button
                onClick={onScrollToForm}
                className={`${tecnologia['btn-primary']} ${tecnologia['btn-primary-large']}`}
              >
                <FaRocket /> {optionData.btn}
              </button>
            </div>
          </div>
        </div>
      </div>
      <ScrollArrow onClick={onScrollDown} direction="down" />
    </section>
  );
}));
SectionDiferenciais.displayName = 'SectionDiferenciais';


const depoimentosData = [
  {
    id: 1,
    nome: 'Joany Peres',
    foto: cliente1,
    estrelas: 5,
    texto: 'É uma boa empresa. Sempre sou muito bem atendido pela Karina Munhoz. Excelente profissional!',
  },
  {
    id: 2,
    nome: 'Julliano Pereira',
    foto: cliente2,
    estrelas: 5,
    texto: 'Excelente trabalho realizado! Todos têm me dado um suporte incrível. Em especial, quero destacar o empenho do Guilherme, que foi fundamental para nos ajudar na abertura da nossa primeira filial.',
  },
  {
    id: 3,
    nome: 'Danielle Mello',
    foto: 'https://ui-avatars.com/api/?name=Danielle+Mello&background=random',
    estrelas: 5,
    texto: 'O profissional Romário Matos me atendeu com excelência de início ao fim da minha regularização. Super recomendo!!!',
  },
];

const Depoimentos = memo(forwardRef(({ selectedId, onScrollDown, onScrollToForm }, ref) => {
  return (
    <section ref={ref} id="depoimentos" className={`${tecnologia.section} ${tecnologia['depoimentos-section']}`}>
      <div className={tecnologia.container}>
        <div className={tecnologia['dep-grid']}>
          {depoimentosData.map((dep) => (
            <div key={dep.id} className={tecnologia['dep-card']}>
              <div className={tecnologia['dep-header']}>
                <img
                  src={dep.foto}
                  alt={dep.nome}
                  className={tecnologia['dep-avatar-img']}
                />
                <div>
                  <div className={tecnologia['dep-name']}>{dep.nome}</div>
                  <div className={tecnologia['dep-stars']}>
                    {'★'.repeat(dep.estrelas)}
                  </div>
                </div>
              </div>
              <p className={tecnologia['dep-text']}>{dep.texto}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}));
Depoimentos.displayName = 'Depoimentos';

/* ── Seção Formulário ── */
const SectionFormulario = memo(forwardRef(({ selectedId }, ref) => {
  const [selectOption, setSelectOption] = useState(selectedId ?? "");

  // ✅ sincroniza quando selectedId muda externamente
  useEffect(() => {
    setSelectOption(selectedId ?? "");
  }, [selectedId]);

  if (!selectedId) return null;

  const selectedOption = OPTION_MAP[selectedId] ?? null;

  return (
    <section ref={ref} id="formulario" className={`${tecnologia.section} ${tecnologia['formulario-section']}`}>
      <div className={tecnologia['container_formulario']}>
        <Formulario
          selectedOption={selectedOption}
          selectOption={selectOption}
          setSelectOption={setSelectOption}
          id="form-prov-dev"
        />
      </div>
    </section>
  );
}));
SectionFormulario.displayName = 'SectionFormulario';

/* ══════════════════════════════════════
   MODAL WHATSAPP
══════════════════════════════════════ */

// ✅ PERF: objeto de cores constante fora do componente
const MODAL_COLORS = {
  bg: '#14171d',
  headerBg: '#1c2128',
  comment: '#57606a',
  close: "#ff0000",
  number: '#9baaf9',
  text: '#c9d1d9',
  green: '#aff5b4',
};

// ✅ PERF: componente estático — nunca precisa re-renderizar
const TerminalDots = memo(() => (
  <div style={{ display: 'flex', gap: '8px', marginRight: '12px' }}>
    <div style={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: '#ff5f56' }} />
    <div style={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: '#ffbd2e' }} />
    <div style={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: '#27c93f' }} />
  </div>
));
TerminalDots.displayName = 'TerminalDots';

// ✅ PERF: estilos do Modal como constante fora do componente — não recria objeto a cada render
const MODAL_STYLES = {
  mask: { backdropFilter: 'blur(4px)' },
  content: {
    backgroundColor: MODAL_COLORS.bg,
    padding: 0,
    overflow: 'hidden',
    border: '1px solid #30363d',
    borderRadius: '12px',
  },
  header: {
    backgroundColor: MODAL_COLORS.headerBg,
    padding: '12px 16px',
    margin: 0,
    borderBottom: '1px solid #30363d',
  },
  body: {
    padding: '24px',
    fontFamily: '"Fira Code", "Courier New", monospace',
  },
};

// ✅ PERF: título do modal como componente estável
const ModalTitle = memo(() => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
    <div style={{ display: 'flex', alignItems: 'center' }}>
      <TerminalDots />
      <span style={{ color: MODAL_COLORS.comment, fontSize: '14px', fontFamily: 'monospace' }}>
        mco-cli · ~/contato
      </span>
    </div>
  </div>
));
ModalTitle.displayName = 'ModalTitle';

function ModalWhatsApp({ open, onClose, selectedId, modalType }) {
  const [userName, setUserName] = useState('');
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [selectOption, setSelectOption] = useState(selectedId ?? "");

  const urlRedirect = `${process.env.PUBLIC_URL}/indexRedirect.html`;

  const screens = useBreakpoint();
  const isMobile = !screens.md;

  useEffect(() => {
    setSelectOption(selectedId ?? "");
  }, [selectedId]);

  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data === 'close_whatsapp_modal') {
        setIsRedirecting(false);
        setUserName('');
        onClose();
      }

      if (event.data?.type === 'open_whatsapp') {
        const link = document.createElement('a');
        link.href = event.data.url;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setIsRedirecting(false);
        setUserName('');
        onClose();
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [onClose]);

  useEffect(() => {
    if (isRedirecting) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'id-redirect-dev',
        eventCategory: 'WhatsApp',
        eventAction: 'Redirect',
        eventLabel: modalType === 'custom' ? 'Dev' : 'Consultor',
        userName: userName,
        selectedId: selectedId,
      });
    }
  }, [isRedirecting, userName, selectedId, modalType]);

  const handleClose = useCallback(() => {
    setUserName('');
    setIsRedirecting(false);
    onClose();
  }, [onClose]);

  const handleOk = useCallback(() => {
    const nomeLimpo = userName.trim();
    if (!nomeLimpo) {
      toast.info('Por favor, digite seu nome.');
      return;
    }
    setIsRedirecting(true);
  }, [userName]);

  const handleUserNameChange = useCallback((e) => setUserName(e.target.value), []);

  const tipoSelecionado = selectedId ? OPTION_MAP[selectedId]?.id : '';

  const okButtonProps = useMemo(() => ({
    style: {
      backgroundColor: MODAL_COLORS.green,
      borderColor: MODAL_COLORS.green,
      color: '#000',
      fontWeight: 'bold',
      fontFamily: 'monospace',
      display: isRedirecting ? 'none' : 'inline-block',
    },
  }), [isRedirecting]);

  const cancelButtonProps = useMemo(() => ({
    style: { backgroundColor: 'transparent', color: MODAL_COLORS.comment, border: 'none' },
  }), []);

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      centered
      width={isMobile ? 320 : 600}
      destroyOnClose
      styles={MODAL_STYLES}
      title={<ModalTitle />}
      closeIcon={<span style={{ color: MODAL_COLORS.close, fontSize: '38px' }}>×</span>}
      footer={null}
    >
      <>
        <Formulario selectOption={selectOption} setSelectOption={setSelectOption} handleClose={handleClose} />
      </>
    </Modal>
  );
}



/* ══════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════ */
export default function TemplateTecnologia() {
  const [selectedId, setSelectedId] = useState(null);
  const [terminalKey, setTerminalKey] = useState(0);

  const [modalWtsOpen, setModalWtsOpen] = useState(false);
  const [modalType, setModalType] = useState('default');

  const navigate = useNavigate();
  const location = useLocation();

  const heroRef = useRef(null);
  const bannerRef = useRef(null);
  const comoFuncionaRef = useRef(null);
  const diferenciaisRef = useRef(null);
  const formularioRef = useRef(null);

  // ✅ PERF: timer do modal automático armazenado em ref para cleanup garantido
  const autoModalTimerRef = useRef(null);

  const pageRef = useScrollReveal(selectedId);

  /* ── Lê url_section da URL na montagem ── */
  useEffect(() => {
    const match = location.pathname.match(/^\/([^/]+)\/?$/);

    if (match) {
      const urlSection = match[1];
      const foundEntry = Object.entries(page.hero.terminal.options).find(
        ([, opt]) => opt.url_section === urlSection
      );
      if (foundEntry) {
        setSelectedId(foundEntry[0]);
      }
    } else {
      setSelectedId(null);
    }
  }, [location.pathname]);

  // ✅ PERF: scrollTo com useCallback estável
  const scrollTo = useCallback((ref) => {
    ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const handleTerminalSubmit = useCallback((id) => {
    setSelectedId(id);
    const urlSection = page.hero.terminal.options[id]?.url_section;
    if (urlSection) {
      navigate(`/${urlSection}`);
    }
    // NÃO chame setTerminalKey aqui — isso destrói e remonta o terminal
  }, [navigate]);

  const handleScrollToForm = useCallback(() => scrollTo(formularioRef), [scrollTo]);
  const handleBannerDown = useCallback(() => scrollTo(comoFuncionaRef), [scrollTo]);
  const handleComoFuncionaDown = useCallback(() => scrollTo(diferenciaisRef), [scrollTo]);
  const handleDiferenciaisDown = useCallback(() => scrollTo(formularioRef), [scrollTo]);

  const handleReset = useCallback(() => {
    setSelectedId(null);
    setTerminalKey(prev => prev + 1);
    navigate('/dev');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [navigate]);

  const handleOpenWhatsApp = useCallback(() => {
    setModalType('default');
    setModalWtsOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setModalWtsOpen(false);
    setModalType('default');
  }, []);

  // ✅ PERF: scroll ao banner com cleanup de timer
  useEffect(() => {
    if (!selectedId || !bannerRef.current) return;
    const timer = setTimeout(() => scrollTo(bannerRef), 100);
    return () => clearTimeout(timer);
  }, [selectedId, scrollTo]);

  /* ✅ PERF: Modal automático após 30s com cleanup garantido */
  useEffect(() => {
    autoModalTimerRef.current = setTimeout(() => {
      setModalWtsOpen((prevOpen) => {
        if (!prevOpen) {
          setModalType('custom');
          return true;
        }
        return prevOpen;
      });
    }, 20000);

    return () => {
      if (autoModalTimerRef.current) clearTimeout(autoModalTimerRef.current);
    };
  }, []);

  return (
    <div ref={pageRef} className={tecnologia.wrapper}>

      {/* ── Hero ── */}
      <section ref={heroRef} id="hero" className={`${tecnologia.section} ${tecnologia.hero}`}>
        <div className={`${tecnologia.blob} ${tecnologia['hero-blob1']}`} aria-hidden="true" />
        <div className={`${tecnologia.blob} ${tecnologia['hero-blob3']}`} aria-hidden="true" />

        <div className={tecnologia.container}>
          <div className={tecnologia['hero-grid']}>
            <div>
              <Logo className={banner['logo-style']} />
              <h1 className={tecnologia['hero-title']}>

                <span className={tecnologia['hero-title-grad']}>{page.hero.title.line2Grad}</span><br />
                <span className={tecnologia['hero-title-muted']}>{page.hero.title.line3Muted}</span>
                <span className={tecnologia['hero-price']}><br />R$149</span>
              </h1>

              <span className={tecnologia['hero-title-line1']}>{page.hero.title.line1}</span>

              {/* ✅ PERF: InlineHtml como componente estável em vez de inline dangerouslySetInnerHTML */}
              <p className={tecnologia['hero-sub']}>
                <InlineHtml str={page.hero.subtitle} />
              </p>
            </div>

            <div className={tecnologia['hero-visual']}>
              <InteractiveTerminal key={terminalKey} onExecuteScroll={handleTerminalSubmit} />
              {page.hero.floatCards.map((card) => (
                <div
                  key={card.id}
                  className={`${tecnologia['float-card']} ${tecnologia[`float-card-${card.id === 'fc1' ? '1' : '2'}`]}`}
                  aria-hidden="true"
                >
                  {card.icon === 'google'
                    ? <a href="https://www.google.com/search?q=meu+contador+online&amp;oq=meu+contador+online&amp;gs_lcrp=EgZjaHJvbWUqBggAEEUYOzIGCAAQRRg7MgYIARBFGEAyBggCEEUYPDIGCAMQRRhBMgYIBBBFGEEyBggFEEUYQdIBCDE5OTVqMGoxqAIAsAIA&amp;sourceid=chrome&amp;ie=UTF-8&amp;sei=bOlCaPf2FcLU1sQPwvT5kAQ#lrd=0x94ce42796cb716dd:0xb0a04ddb381cf1a5,1" target="_blank" rel="nofollow noopener" class="hover:opacity-80 transition-opacity" aria-label="Ver avaliações no Google">
                      <img src={google} alt="Google" decoding="sync" fetchpriority="high" />
                    </a>

                    : <a href='https://www.reclameaqui.com.br/empresa/meu-contador-online/' target="_blank" rel="nofollow noopener" aria-label="Ver avaliações no Reclame Aqui">
                      <img src={reclameaqui} alt="Reclame Aqui" decoding="sync" fetchpriority="high" />
                    </a>
                  }
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Banner ── */}
      <Banner
        ref={bannerRef}
        selectedId={selectedId}
        onReset={handleReset}
        onScrollDown={handleBannerDown}
        onScrollToForm={handleScrollToForm}
      />

      {/* ── Como Funciona ── */}
      <SectionComoFunciona
        ref={comoFuncionaRef}
        selectedId={selectedId}
        onScrollDown={handleComoFuncionaDown}
        onScrollToForm={handleScrollToForm}
      />

      {/* ── Diferenciais ── */}
      <SectionDiferenciais
        ref={diferenciaisRef}
        selectedId={selectedId}
        onScrollDown={handleDiferenciaisDown}
        onScrollToForm={handleScrollToForm}
      />

      <Depoimentos />

      {/* ── Formulário ── */}
      <SectionFormulario
        ref={formularioRef}
        selectedId={selectedId}
      />

      <Footer />

      {/* ── Botão flutuante WhatsApp ── */}
      {/*<WhatsAppButton onClick={handleOpenWhatsApp} />*/}

      {/* ── Modal WhatsApp ── */}
      <ModalWhatsApp
        open={modalWtsOpen}
        onClose={handleCloseModal}
        selectedId={selectedId}
        modalType={modalType}
      />

    </div>
  );
}